import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { ExpiryThresholdsApi } from '../services/expiryThresholdsApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import './ExpiryThresholds.css';
import './StockInForm-module.css';
import { StandardCard, StandardSearch, StandardScrollStyles } from '../styles/standardStyles';
import InlineToggle from './InlineToggle';

// Usage-style dropdown copied to match StockIn exact styles
const UsageStyleDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select',
  style = {},
  className = ''
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);

  const normalized = options.map(opt =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  );

  const current = normalized.find(o => o.value === value) || null;

  useEffect(() => {
    const onDocDown = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <div
      className={`relative ${className}`}
      ref={ref}
      style={{ position: 'relative', width: '100%', ...(style || {}) }}
    >
      <div className="shift-select" onClick={() => setOpen(o => !o)}>
        <div className="input-text-wrapper">
          <span className="shift-details">
            {current ? (
              <>
                <span className="shift-name">{current.label}</span>
              </>
            ) : (
              <span className="shift-name" style={{ color: '#888' }}>{placeholder}</span>
            )}
          </span>
        </div>
        <span className="chevron-down" />
      </div>
      {open && (
        <div
          ref={menuRef}
          className="shift-dropdown-options"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e)=>e.stopPropagation()}
        >
          <div className="select-list" style={{ maxHeight: 200, overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {normalized.map(opt => (
              <div
                key={String(opt.value)}
                className="shift-option"
                onClick={() => { onChange(opt.value); setOpen(false); }}
              >
                <span className="shift-name">{opt.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function ExpiryThresholds({ goTo }) {
  const [category, setCategory] = useState('All Categories');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const searchInputRef = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true); 
    setError(null);
    try {
      const [itemsData, statsData] = await Promise.all([
        ExpiryThresholdsApi.listInventoryItemThresholds(),
        ExpiryThresholdsApi.getStats()
      ]);
      
      setRows(itemsData.map(r => ({
        id: r.threshold_id,
        item_id: r.item_id,
        name: r.item_name,
        category: r.category,
        expiry_required: r.expiry_required || false,
        // H2 — stock-in LEARNS that an item gets expiry dates, and that used to be written
        // straight into expiry_required: the first person to OPTIONALLY type a date made
        // expiry mandatory for that item forever, silently. It is a suggestion now, and this
        // is the screen where the owner sees it and decides.
        expiry_suggested: r.expiry_suggested || false,
        days_before_alert: r.days_before_expiry_alert || 7,
        is_active: r.is_active !== false,
        has_expiry_history: r.has_expiry_history || false,
        created_at: r.created_at,
        updated_at: r.updated_at
      })));
      
      setStats(statsData);
    } catch (e) { 
      setError(e.message); 
    } finally { 
      setLoading(false); 
    }
  }, []);

  useEffect(() => { 
    loadData(); 
  }, [loadData]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let base = rows;
    if (category !== 'All Categories') {
      base = base.filter(r => (r.category || '').toLowerCase() === category.toLowerCase());
    }
    if (!q) return base;
    return base.filter(r => r.name.toLowerCase().includes(q));
  }, [rows, query, category]);

  const onToggleExpiryRequired = async (itemId) => {
    try {
      const currentRow = rows.find(r => r.item_id === itemId);
      const newValue = !currentRow.expiry_required;
      
      // Optimistic update
      setRows(prev => prev.map(r => 
        r.item_id === itemId ? { ...r, expiry_required: newValue } : r
      ));
      
      // API call
      const updated = await ExpiryThresholdsApi.updateInventoryItemThreshold(itemId, { 
        item_id: itemId,
        expiry_required: newValue,
        days_before_expiry_alert: currentRow.days_before_alert,
        is_active: currentRow.is_active
      });
      
      // Update with server response
      setRows(prev => prev.map(r => 
        r.item_id === itemId ? { 
          ...r, 
          id: updated.id || r.id,
          expiry_required: newValue 
        } : r
      ));
      
      // Reload stats
      const newStats = await ExpiryThresholdsApi.getStats();
      setStats(newStats);
    } catch(e) { 
      setError(e.message);
      // Revert on error
      loadData();
    }
  };

  const onDaysChange = async (itemId, newDays) => {
    try {
      const currentRow = rows.find(r => r.item_id === itemId);
      
      // Optimistic update
      setRows(prev => prev.map(r => 
        r.item_id === itemId ? { ...r, days_before_alert: newDays } : r
      ));
      
      // API call
      const updated = await ExpiryThresholdsApi.updateInventoryItemThreshold(itemId, { 
        item_id: itemId,
        expiry_required: currentRow.expiry_required,
        days_before_expiry_alert: newDays,
        is_active: currentRow.is_active
      });
      
      // Update with server response
      setRows(prev => prev.map(r => 
        r.item_id === itemId ? { 
          ...r, 
          id: updated.id || r.id 
        } : r
      ));
    } catch(e) { 
      setError(e.message);
      loadData();
    }
  };

  return (
    <div className="et-page">
      <StandardScrollStyles />
      <div className="et-title-wrap" style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '10px' }}>
        <button 
          onClick={() => goTo && goTo('stock-in-scan')}
          style={{
            width: '32px',
            height: '32px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '20px', color: '#333' }} />
        </button>
        <h2 className="et-title" style={{ margin: 0 }}>Expiry Tracking Configuration</h2>
      </div>

      <div className="et-toolbar" style={{ marginTop: 8 }}>
        <UsageStyleDropdown
          value={category}
          onChange={setCategory}
          options={[
            'All Categories',
            'Vegetables',
            'Fruits',
            'Dairy',
            'Meat',
            'Seafood',
            'Bakery',
            'Beverages',
            'Spices',
            'Grains',
            'Oils',
            'Condiments',
            'Frozen',
            'Dry Goods',
            'Other'
          ]}
          placeholder="Select Category"
        />
        <div className="et-toolbar-row2">
          <StandardSearch
            value={query}
            onChange={setQuery}
            onClear={() => setQuery('')}
            placeholder="Search Items"
            inputRef={searchInputRef}
          />
          <div />
        </div>
      </div>

      <div>
        <div className="et-thead">
          <div>S. No</div>
          <div>Inventory Item</div>
          <div style={{ textAlign: 'center' }}>Expiry Required</div>
        </div>
        <div className={filtered.length > 4 ? 'et-scrollable-list' : ''}>
          {filtered.map((r, idx) => (
            <div key={r.item_id} className="et-row">
              <div>{idx + 1}</div>
              <div className="et-cell">
                <div className="et-item-name" title={r.name}>
                  {r.name}
                </div>
                {r.expiry_suggested && !r.expiry_required && (
                  <span
                    className="et-suggested-chip"
                    title="Expiry dates have been entered for this item during stock-in. Turn the toggle on if it should be required."
                    style={{
                      marginLeft: 8,
                      padding: '2px 7px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#b45309',
                      background: '#fef3c7',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Suggested
                  </span>
                )}
              </div>
              <div style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40px' }}>
                <InlineToggle 
                  value={r.expiry_required} 
                  onChange={() => onToggleExpiryRequired(r.item_id)} 
                />
              </div>
            </div>
          ))}
          {filtered.length === 0 && !loading && (
            <div style={{ padding: 16, textAlign: 'center', color: '#777' }}>No Items Found</div>
          )}
          {loading && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              padding: '40px 20px',
              gap: '16px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{
                color: '#666',
                fontSize: '16px',
                textAlign: 'center',
                margin: 0
              }}>Loading...</p>
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
          )}
        </div>
      </div>

      {error && <div style={{ marginTop: 16, color: '#c0392b', padding: '12px', background: '#fee', borderRadius: '8px' }}>Error: {error}</div>}
    </div>
  );
}
